import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import ProtectedRoute from '../components/ProtectedRoute';
import { useAlert } from '../contexts/AlertContext';
import { useWorkSites } from '../hooks/useWorkSites';
import { dashboardApi, DashboardData, MGMT_OPEN, OPEX_DUE, toMan } from '../services/dashboardApi';
import { AppCard, linkedAppsApi, LinkTarget } from '../services/linkedAppsApi';
import { openWorkSite } from '../utils/workSites';

/**
 * 경영현황 (GRK_admin#97) — 관리자 첫 화면. 숫자는 GET /api/dashboard(합계 · 건수만),
 * 아래 연결 앱 카드는 「전체 현황」과 같은 linkedAppsApi.summary · 바로 이동.
 */
const DashboardPage: React.FC = () => {
  const ws = useWorkSites();
  return (
    <ProtectedRoute allow={ws.loading ? null : ws.allowed}>
      {MGMT_OPEN ? (
        <Dashboard sites={ws.sites} />
      ) : (
        <div className="p-6 lg:p-8">
          <div className="bg-white rounded-lg shadow border border-slate-200 p-8 text-center">
            <h1 className="text-xl font-bold text-slate-800 mb-2">준비중</h1>
            <p className="text-sm text-slate-500">경영현황은 준비 중입니다.</p>
          </div>
        </div>
      )}
    </ProtectedRoute>
  );
};

type Tone = 'ok' | 'warn' | 'todo' | 'bad';
const TONE: Record<Tone, string> = {
  ok: 'bg-emerald-50 text-emerald-600',
  warn: 'bg-amber-50 text-amber-600',
  todo: 'bg-slate-100 text-slate-700',
  bad: 'bg-red-50 text-red-600',
};
const DOT: Record<Tone, string> = { ok: '#059669', warn: '#d97706', todo: '#94a3b8', bad: '#dc2626' };
const WEEK = ['일', '월', '화', '수', '목', '금', '토'];

const lastDay = (y: number, m: number) => new Date(y, m, 0).getDate();
const won = (n: number) => n.toLocaleString('ko-KR');
/** 만원 단위 값 → 18.6억 · 3,100만 (기준은 만원, 1억 이상만 억으로 줄인다) */
function man(n: number): string {
  if (n === 0) return '0';
  if (Math.abs(n) >= 10000) return `${(n / 10000).toFixed(1)}억`;
  return `${Math.round(n).toLocaleString('ko-KR')}만`;
}

const Dashboard: React.FC<{ sites: LinkTarget[] }> = ({ sites }) => {
  const { showToast } = useAlert();
  const navigate = useNavigate();
  const now = new Date();
  const [sel, setSel] = useState({ year: now.getFullYear(), month: now.getMonth() + 1 });
  const [data, setData] = useState<DashboardData | null>(null);
  const [cards, setCards] = useState<AppCard[]>([]);
  const [appsLoading, setAppsLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      setData(await dashboardApi.get(sel.year, sel.month));
    } catch (e) {
      showToast(`경영현황을 불러오지 못했습니다: ${(e as Error).message}`, 'error');
    }
  }, [sel, showToast]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    linkedAppsApi
      .summary()
      .then(setCards)
      .catch(() => setCards([]))
      .finally(() => setAppsLoading(false));
  }, []);

  const go = (target: LinkTarget, next?: string) =>
    openWorkSite(target, next).catch((e) => showToast(`이동하지 못했습니다: ${(e as Error).message}`, 'error'));

  // 최근 12개월
  const options = useMemo(() => {
    const base = new Date();
    return Array.from({ length: 12 }, (_, i) => {
      const d = new Date(base.getFullYear(), base.getMonth() - i, 1);
      return { year: d.getFullYear(), month: d.getMonth() + 1, label: i === 0 ? '이번 달' : `${d.getFullYear()}년 ${d.getMonth() + 1}월` };
    });
  }, []);

  const today = data ? new Date(`${data.today}T00:00:00`) : now;
  const head = `${sel.year}년 ${sel.month}월 · ${today.getMonth() + 1}/${today.getDate()}(${WEEK[today.getDay()]}) 기준`;

  return (
    <div className="p-6 lg:p-8 flex flex-col gap-5 min-w-0">
      <header className="flex flex-wrap items-end gap-3">
        <div>
          <h1 className="text-[22px] font-bold text-slate-900">경영현황</h1>
          <div className="text-[13px] text-slate-500">{head}</div>
        </div>
        <div className="ml-auto">
          <select
            aria-label="기간"
            className="border border-slate-200 bg-white rounded-lg px-3 py-1.5 text-[13px]"
            value={`${sel.year}-${sel.month}`}
            onChange={(e) => {
              const [y, m] = e.target.value.split('-').map(Number);
              setSel({ year: y, month: m });
            }}
          >
            {options.map((o) => (
              <option key={`${o.year}-${o.month}`} value={`${o.year}-${o.month}`}>
                {o.label}
              </option>
            ))}
          </select>
        </div>
      </header>

      {!data ? (
        <div className="grid gap-3.5 grid-cols-1 xl:grid-cols-[5fr_7fr]">
          <div className="h-64 rounded-xl bg-white border border-slate-200 animate-pulse" />
          <div className="h-64 rounded-xl bg-white border border-slate-200 animate-pulse" />
        </div>
      ) : (
        <>
          <section className="grid gap-3.5 grid-cols-1 xl:grid-cols-[minmax(0,5fr)_minmax(0,7fr)]">
            <Tasks data={data} navigate={navigate} />
            <Trend data={data} />
          </section>

          <section className="grid gap-3.5 grid-cols-1 md:grid-cols-3">
            <Card title="사업" src="PPE · 단위: 만원">
              <Mini items={[
                ['진행중', `${data.business.ongoing}건`],
                ['계획', `${data.business.planned}건`],
                ['올해 완료', `${data.business.completedThisYear}건`],
              ]} />
              <div className="flex justify-between text-[12.5px] text-slate-700">
                <span>올해 매출 (입금 예정일 기준)</span>
                <span className="tabular-nums">{man(data.business.revenueThisYear)}</span>
              </div>
              <div className="text-[11.5px] text-slate-400">
                이번 달 종료 {data.business.endingThisMonth}건
                {data.business.nextEnding &&
                  ` · ${data.business.nextEnding.name} ${Number(data.business.nextEnding.endDate.slice(5, 7))}/${Number(data.business.nextEnding.endDate.slice(8, 10))}`}
              </div>
            </Card>

            <Card title="인사" src="인력관리 · 근태관리">
              <Mini items={[
                ['재직', `${data.hr.active}명`],
                ['오늘 휴가', `${data.hr.onLeaveToday}명`],
                ['휴가 승인 대기', `${data.hr.pendingLeave}건`, data.hr.pendingLeave > 0],
              ]} />
              <div className="text-[11.5px] text-slate-400">이번 달 연차 사용 {data.hr.annualUsedDays}일</div>
            </Card>

            <Card title="법인카드" src="사용내역 · 정산 · 단위: 만원">
              <Mini items={[
                [`${data.card.prevMonth}월분 합계`, data.card.prevTotal === null ? '—' : man(toMan(data.card.prevTotal))],
                [`${data.month}월분 확정`, `${data.card.confirmed}/${data.card.target}명`, data.card.confirmed < data.card.target],
              ]} />
            </Card>
          </section>
        </>
      )}

      <section className="grid gap-3.5 grid-cols-1 md:grid-cols-3">
        <AppBox title="채용" src="hire" target="hire" action="관리자 페이지 ↗" next="/admin" sites={sites} cards={cards} loading={appsLoading} go={go} />
        <AppBox title="컨설팅 · 사업" src="project" target="project" action="열기 ↗" sites={sites} cards={cards} loading={appsLoading} go={go} />
        <AppBox title="투자 관리" src="investment" target="investment" action="열기 ↗" sites={sites} cards={cards} loading={appsLoading} go={go} />
      </section>
    </div>
  );
};

const Card: React.FC<{ title: string; src: string; action?: React.ReactNode; children: React.ReactNode }> = ({ title, src, action, children }) => (
  <div className="bg-white border border-slate-200 rounded-xl px-[18px] py-4 min-w-0 flex flex-col gap-3">
    <div className="flex items-center gap-2">
      <h2 className="text-sm font-bold text-slate-900">{title}</h2>
      <span className="ml-auto text-[11px] text-slate-400">{src}</span>
      {action}
    </div>
    {children}
  </div>
);

const Mini: React.FC<{ items: [string, string, boolean?][] }> = ({ items }) => (
  <div className="flex flex-wrap gap-2.5">
    {items.map(([k, v, warn]) => (
      <div key={k} className="flex-1 min-w-[90px] bg-slate-50 rounded-lg px-2.5 py-2">
        <div className="text-[11.5px] text-slate-500">{k}</div>
        <div className={`text-[17px] font-semibold tabular-nums ${warn ? 'text-amber-600' : 'text-slate-900'}`}>{v}</div>
      </div>
    ))}
  </div>
);

// ── 이번 달 업무 ─────────────────────────────────────────

interface TaskRow {
  key: string;
  day: number;
  dayLabel: string;
  name: string;
  short: string;
  desc: string;
  tone: Tone;
  status: string;
  path: string;
  /** 자동이체 — 줄에 「자동」 표시 */
  auto?: boolean;
}

/** 4대보험 완료 판단 기한 — 출금은 10일이지만 급여대장 기입은 급여(25일)와 같이 본다 */
const INSURANCE_DUE = 25;

function buildTasks(d: DashboardData): { rows: TaskRow[]; todayDay: number | null; last: number } {
  const last = lastDay(d.year, d.month);
  const t = new Date(`${d.today}T00:00:00`);
  const cur = t.getFullYear() * 12 + t.getMonth() + 1;
  const sel = d.year * 12 + d.month;
  const todayDay = cur === sel ? t.getDate() : null;
  // 선택한 달이 지났으면 모든 기준일이 지난 것
  const passed = (day: number) => (sel < cur ? true : sel > cur ? false : t.getDate() > day);
  const late = (tone: Tone, day: number): Tone => (tone !== 'ok' && passed(day) ? 'bad' : tone);
  const opexDay = OPEX_DUE === 'last' ? last : OPEX_DUE;

  const c = d.tasks.card;
  const cardTone: Tone = c.consolidated ? 'ok' : c.confirmed > 0 ? 'warn' : 'todo';
  const p = d.tasks.payroll;
  const payTone: Tone = p.collected && p.transferFile ? 'ok' : p.collected ? 'warn' : 'todo';
  const daysLeft = opexDay - (todayDay ?? 0);

  const rows: TaskRow[] = [
    {
      key: 'card', day: 7, dayLabel: '7일', name: '법인카드', short: '카드',
      desc: '개인별 사용내역 올리기 → 확정 → 취합 · 이체파일',
      tone: late(cardTone, 7),
      status: c.consolidated ? `${c.month}월분 취합 완료` : `확정 ${c.confirmed}/${c.target}명`,
      path: '/corporate-card',
    },
    {
      // 공단 출금은 10일 자동이체 — 급여대장 기입은 급여와 같은 25일까지 보면 된다
      key: 'insurance', day: 10, dayLabel: '10일', name: '4대보험', short: '4대보험', auto: true,
      desc: '공단 출금액 조회 → 급여대장에 총액 기입',
      tone: late(d.tasks.insurance.entered ? 'ok' : 'todo', INSURANCE_DUE),
      status: d.tasks.insurance.entered ? '기입됨' : passed(INSURANCE_DUE) ? '미기입' : '출금 · 기입 대기',
      path: '/payroll',
    },
    {
      key: 'payroll', day: 25, dayLabel: '25일', name: '급여', short: '급여',
      desc: '급여대장 수집(혜움) → 이체파일 생성',
      tone: late(payTone, 25),
      status: p.collected && p.transferFile ? '수집 · 이체파일 완료' : p.collected ? '수집 완료 · 이체파일 전' : '미수집',
      path: '/payroll',
    },
    {
      key: 'opex', day: opexDay, dayLabel: OPEX_DUE === 'last' ? '말일' : `${opexDay}일`, name: 'OPEX',
      short: OPEX_DUE === 'last' ? '말일 OPEX' : 'OPEX',
      desc: '해당 월 항목 입력 → 확정',
      tone: late(d.tasks.opex.confirmed ? 'ok' : 'todo', opexDay),
      status: d.tasks.opex.confirmed
        ? '확정'
        : todayDay !== null && daysLeft >= 0
          ? `미확정 · D-${daysLeft}`
          : '미확정',
      path: '/opex',
    },
  ];
  return { rows, todayDay, last };
}

const Tasks: React.FC<{ data: DashboardData; navigate: (p: string) => void }> = ({ data, navigate }) => {
  const { rows, todayDay, last } = buildTasks(data);
  const x = (day: number) => 20 + ((day - 1) / (last - 1)) * 580;
  const progress = todayDay !== null ? x(todayDay) : data.year * 12 + data.month < Number(data.today.slice(0, 4)) * 12 + Number(data.today.slice(5, 7)) ? 600 : 20;

  return (
    <Card title="이번 달 업무" src={`${data.month}월 · 매달 반복`}>
      <svg className="w-full h-auto block" viewBox="0 0 620 64" role="img" aria-label={`${data.month}월 업무 기준일과 오늘 위치`}>
        <line x1="20" y1="30" x2="600" y2="30" stroke="#e2e8f0" strokeWidth="4" strokeLinecap="round" />
        {progress > 20 && <line x1="20" y1="30" x2={progress} y2="30" stroke="#c7d2fe" strokeWidth="4" strokeLinecap="round" />}
        <g fontSize="11" fill="#94a3b8" textAnchor="middle">
          <text x="20" y="58">1</text>
          <text x={x(15)} y="58">15</text>
          <text x="600" y="58">{last}</text>
        </g>
        <g fontSize="11.5" textAnchor="middle">
          {rows.map((r) => (
            <g key={r.key}>
              {r.tone === 'ok' ? (
                <circle cx={x(r.day)} cy="30" r="7" fill={DOT.ok} />
              ) : (
                <circle cx={x(r.day)} cy="30" r="7" fill="#ffffff" stroke={DOT[r.tone]} strokeWidth="2.5" />
              )}
              <text x={Math.min(x(r.day), 572)} y="14" fill="#334155">
                {r.key === 'opex' ? r.short : `${r.day} ${r.short}`}
              </text>
            </g>
          ))}
        </g>
        {todayDay !== null && (
          <>
            <line x1={x(todayDay)} y1="20" x2={x(todayDay)} y2="40" stroke="#0f172a" strokeWidth="2" />
            <text x={x(todayDay)} y="58" fontSize="11" textAnchor="middle" fill="#0f172a" fontWeight="600">
              오늘 {todayDay}
            </text>
          </>
        )}
      </svg>
      <ul className="flex flex-col">
        {rows.map((r, i) => (
          <li key={r.key} className={i ? 'border-t border-slate-100' : ''}>
            <button
              type="button"
              onClick={() => navigate(r.path)}
              className="w-full grid grid-cols-[52px_minmax(0,1fr)_auto] gap-2.5 items-center py-2.5 text-left hover:bg-slate-50 rounded"
            >
              <span className={`text-xs font-semibold text-center rounded-md py-0.5 ${r.tone === 'bad' ? 'bg-red-50 text-red-600' : 'bg-indigo-50 text-indigo-600'}`}>
                {r.dayLabel}
              </span>
              <span className="text-[13.5px] text-slate-900 min-w-0">
                {r.name}
                {r.auto && <span className="ml-1.5 align-[1px] text-[10.5px] font-semibold rounded px-1.5 py-px bg-slate-100 text-slate-500">자동</span>}
                <small className="block text-[11.5px] text-slate-400 truncate">{r.desc}</small>
              </span>
              <span className={`text-[11.5px] font-semibold rounded px-[7px] py-0.5 whitespace-nowrap ${TONE[r.tone]}`}>
                {r.status}
                {r.tone === 'bad' && ' · 기한 지남'}
              </span>
            </button>
          </li>
        ))}
      </ul>
    </Card>
  );
};

// ── 매출 · 비용 추이 ─────────────────────────────────────

function niceMax(v: number): number {
  if (v <= 0) return 10000;
  const step = 10 ** Math.floor(Math.log10(v));
  return Math.ceil(v / step) * step;
}

const Trend: React.FC<{ data: DashboardData }> = ({ data }) => {
  const rows = data.trend.map((t) => ({ ...t, opex: toMan(t.opex) })); // 둘 다 만원
  const max = niceMax(Math.max(...rows.map((r) => Math.max(r.revenue, r.opex))));
  const y = (v: number) => 200 - (v / max) * 180;
  const ticks = [0, 1, 2, 3, 4].map((i) => (max * i) / 4);

  return (
    <Card title="매출 · 비용 추이" src="PPE · OPEX · 최근 6개월 · 단위: 만원">
      <div className="flex flex-wrap gap-3.5 text-xs text-slate-500">
        <span className="inline-flex items-center gap-1.5"><i className="inline-block w-2.5 h-2.5 rounded-sm" style={{ background: '#a5b4fc' }} />매출 (PPE 입금 예정일 기준)</span>
        <span className="inline-flex items-center gap-1.5"><i className="inline-block w-2.5 h-2.5 rounded-sm" style={{ background: '#cbd5e1' }} />OPEX (월 합계)</span>
      </div>
      <svg className="w-full h-auto block" viewBox="0 0 600 230" role="img" aria-label="최근 6개월 매출과 OPEX 추이">
        <g stroke="#f1f5f9" strokeWidth="1">
          {ticks.map((t, i) => (
            <line key={i} x1="40" y1={y(t)} x2="590" y2={y(t)} stroke={i === 0 ? '#e2e8f0' : '#f1f5f9'} />
          ))}
        </g>
        <g fontSize="11" fill="#94a3b8" textAnchor="end">
          {ticks.map((t, i) => (
            <text key={i} x="34" y={y(t) + 4}>{man(t)}</text>
          ))}
        </g>
        {rows.map((r, i) => {
          const cx = 66 + i * 92;
          const last = i === rows.length - 1;
          return (
            <g key={`${r.year}-${r.month}`}>
              <rect x={cx} y={y(r.revenue)} width="22" height={200 - y(r.revenue)} rx="3" fill="#a5b4fc">
                <title>{`${r.month}월 매출 ${won(Math.round(r.revenue))}만원`}</title>
              </rect>
              <rect x={cx + 25} y={y(r.opex)} width="22" height={200 - y(r.opex)} rx="3" fill="#cbd5e1">
                <title>{`${r.month}월 OPEX ${won(Math.round(r.opex))}만원`}</title>
              </rect>
              <text x={cx + 23} y="220" fontSize="12" textAnchor="middle" fill={last ? '#0f172a' : '#64748b'} fontWeight={last ? 600 : 400}>
                {r.month}월
              </text>
            </g>
          );
        })}
      </svg>
    </Card>
  );
};

// ── 연결 앱 ─────────────────────────────────────────────

const AppBox: React.FC<{
  title: string;
  src: string;
  target: LinkTarget;
  action: string;
  next?: string;
  sites: LinkTarget[];
  cards: AppCard[];
  loading: boolean;
  go: (t: LinkTarget, next?: string) => void;
}> = ({ title, src, target, action, next, sites, cards, loading, go }) => {
  const card = cards.find((c) => c.target === target);
  // 키가 없어 연결 설정이 안 된 앱은 「준비중」
  if (!sites.includes(target) || card?.state === 'unconfigured') return <Pending title={title} src={src} what="현황 · 바로 이동" />;
  const ok = card?.state === 'ok' ? card : null;

  return (
    <Card
      title={title}
      src={src}
      action={
        <button type="button" onClick={() => go(target, next)} className="ml-2 text-xs font-semibold text-indigo-600 border border-indigo-200 rounded-md px-2 py-0.5 whitespace-nowrap hover:bg-indigo-50">
          {action}
        </button>
      }
    >
      {loading ? (
        <div className="h-28 rounded-lg bg-slate-50 animate-pulse" />
      ) : ok ? (
        <div className="flex flex-col">
          {ok.summary.items.map((it, i) => (
            <button
              key={it.key}
              type="button"
              onClick={() => go(target, it.link)}
              className={`flex justify-between gap-2 py-2 px-0.5 text-[13px] text-slate-700 hover:bg-slate-50 text-left ${i ? 'border-t border-slate-100' : ''}`}
            >
              <span>{it.label}</span>
              <b className="text-[15px] font-semibold text-slate-900 tabular-nums">
                {typeof it.value === 'number' ? won(it.value) : it.value}
              </b>
            </button>
          ))}
        </div>
      ) : (
        <p className="text-sm text-slate-500">요약을 받지 못했습니다. 열기 · 바로 가기는 됩니다.</p>
      )}
    </Card>
  );
};

const Pending: React.FC<{ title: string; src: string; what: string }> = ({ title, src, what }) => (
  <div
    className="border border-slate-200 rounded-xl px-[18px] py-4 min-w-0 flex flex-col gap-3"
    style={{ background: 'repeating-linear-gradient(135deg,#ffffff 0 10px,#f8fafc 10px 20px)' }}
  >
    <div className="flex items-center gap-2">
      <h2 className="text-sm font-bold text-slate-900">{title}</h2>
      <span className="ml-auto text-[11px] text-slate-400">{src}</span>
      <span className="ml-2 text-[10.5px] rounded px-[7px] py-px bg-slate-100 text-slate-400">준비중</span>
    </div>
    <div className="flex-1 flex flex-col items-center justify-center gap-1 text-center min-h-[120px] text-[12.5px] text-slate-400">
      <strong className="text-[13px] text-slate-500">{what}</strong>앱이 열리면 같은 방식으로 표시
    </div>
  </div>
);

export default DashboardPage;
